import { useState, useEffect, useCallback, useRef } from 'react';
import { useLicenseStore } from '../store/licenseStore';
import { useAuthStore } from '../store/authStore';
import { useHasProAccess } from './useHasProAccess';
import { isBrowserMode } from '../../../features/browser-mode/browserModeUtils';
import { shouldShowTrialExpiryDialog, trialExpiryIso } from '../services/trialService';
import { trackTrialRequested } from '../services/trialRequestedEvent';
import {
  extendServerTrial,
  type TrialExtensionReason,
} from '../services/entitlementTokenService';

/** ISO expiry the dialog was already shown for — once per distinct expiry. */
const SHOWN_FOR_KEY = 'sql-studio-trial-expiry-prompt';
/**
 * Local mirror of the server's once-per-ACCOUNT extension gate (key is
 * suffixed with the user id — a second account on the same machine keeps its
 * own offer), so the SECOND expiry (of the extended trial) offers
 * upgrade/personal only instead of a feedback form the server would reject.
 * Per-machine only — a cross-machine attempt still gets the server's
 * ALREADY_EXTENDED, handled gracefully in the submit path.
 */
const EXTENSION_USED_KEY_PREFIX = 'sql-studio-trial-extension-used';

export type TrialExtensionSubmitResult = {
  success: boolean;
  /** True when trial days were actually granted (false: already a subscriber). */
  extended?: boolean;
  error?: string;
};

/**
 * localStorage read that distinguishes "no value" (null) from "storage
 * unavailable" (undefined) — the gating below must go SILENT when storage
 * throws, because without a writable shown-flag the dialog would otherwise
 * re-nag on every boot.
 */
function readLocalStorage(key: string): string | null | undefined {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return undefined;
  }
}

/**
 * False only when we can positively attribute a spent extension to the GIVEN
 * account; signed-out or storage-failure cases stay available — the server
 * enforces the real gate either way.
 */
function isExtensionUsedFor(userId: string | undefined): boolean {
  if (!userId) return false;
  return readLocalStorage(`${EXTENSION_USED_KEY_PREFIX}:${userId}`) === '1';
}

function markExtensionUsedFor(userId: string | undefined): void {
  if (!userId) return;
  try {
    window.localStorage.setItem(`${EXTENSION_USED_KEY_PREFIX}:${userId}`, '1');
  } catch {
    // Server enforces the real gate.
  }
}

/**
 * The trial-expiry dialog: shows ONCE when a trial has ended (per distinct
 * expiry date) with three ways forward — upgrade to Pro, stay on Personal, or
 * answer one "what would get you to Pro?" question for 14 more trial days
 * (once per account, server-enforced). Field data behind it: trial expiry had
 * converted 0/77 with no surface at that moment at all.
 */
export function useTrialExpiryDialog() {
  const [isOpen, setIsOpen] = useState(false);

  const trialState = useLicenseStore((state) => state.getTrialState());
  const trialExpiresAt = useLicenseStore((state) => state.state.trialExpiresAt);
  const isInitialized = useLicenseStore((state) => state.isInitialized);
  const isResolvingEntitlement = useAuthStore((state) => state.isResolvingEntitlement);
  // Subscribed (not read via getState) so `extensionAvailable` recomputes on
  // account switches.
  const userId = useAuthStore((state) => state.user?.id);
  const hasProAccess = useHasProAccess();

  // Mirrors isOpen so the "shown" event fires only on a closed→open edge.
  const openRef = useRef(false);
  // The expiry the dialog OPENED for. Every shown-flag stamp uses this — the
  // store's expiry can move underneath an open dialog (entitlement sync,
  // an extension applied on another machine), and stamping the moved value
  // would swallow the replacement expiry's own dialog.
  const promptedExpiryIsoRef = useRef<string | undefined>(undefined);

  /**
   * Stamp the shown-flag for a SPECIFIC expiry. The extension path must pass
   * the PRE-extension expiry captured before `startTrialFromServer` rewrites
   * the store — stamping the store's current value there would record the
   * NEW expiry and permanently swallow the extended trial's own expiry
   * dialog.
   */
  const markShownFor = useCallback((expiryIso: string | undefined) => {
    if (!expiryIso) return;
    try {
      window.localStorage.setItem(SHOWN_FOR_KEY, expiryIso);
    } catch {
      // Storage unavailable — the gating effect already goes silent then.
    }
  }, []);

  const trackClick = useCallback(
    (action: 'upgrade' | 'continue_personal' | 'give_feedback' | 'dismiss') => {
      window.electronAPI?.analytics
        ?.track('ui:trial_expiry_dialog_click', { action })
        ?.catch(() => {});
    },
    []
  );

  useEffect(() => {
    if (isBrowserMode()) return;
    const isTestMode =
      typeof window !== 'undefined' && window.electronAPI?.testMode?.isTestMode?.() === true;
    if (isTestMode && readLocalStorage('sql-studio-e2e-allow-trial-expiry') !== '1') return;

    if (hasProAccess && openRef.current) {
      // Reactive close: an upgrade or extension mid-dialog ends the moment.
      openRef.current = false;
      setIsOpen(false);
      return;
    }

    if (openRef.current) return;
    const shownFor = readLocalStorage(SHOWN_FOR_KEY);
    // Storage unavailable → the shown-flag could never persist; stay silent
    // instead of re-nagging on every boot.
    if (shownFor === undefined) return;
    if (
      !shouldShowTrialExpiryDialog({
        isInitialized,
        hasProAccess,
        isResolvingEntitlement,
        trialState,
        trialExpiresAt,
        shownForExpiresAt: shownFor,
      })
    ) {
      return;
    }

    openRef.current = true;
    promptedExpiryIsoRef.current = trialExpiryIso(trialExpiresAt);
    setIsOpen(true);
    window.electronAPI?.analytics
      ?.track('ui:trial_expiry_dialog_shown', {
        extension_available: !isExtensionUsedFor(useAuthStore.getState().user?.id),
      })
      ?.catch(() => {});
  }, [isInitialized, hasProAccess, isResolvingEntitlement, trialState, trialExpiresAt]);

  const close = useCallback(() => {
    markShownFor(promptedExpiryIsoRef.current);
    openRef.current = false;
    setIsOpen(false);
  }, [markShownFor]);

  const onUpgrade = useCallback(() => {
    trackClick('upgrade');
    close();
  }, [trackClick, close]);

  const onContinuePersonal = useCallback(() => {
    trackClick('continue_personal');
    close();
  }, [trackClick, close]);

  const onDismiss = useCallback(() => {
    trackClick('dismiss');
    close();
  }, [trackClick, close]);

  const onFeedbackOpened = useCallback(() => {
    trackClick('give_feedback');
  }, [trackClick]);

  const onSubmitFeedback = useCallback(
    async (
      reason: TrialExtensionReason,
      details: string | null
    ): Promise<TrialExtensionSubmitResult> => {
      // The account that is actually submitting — everything local the
      // response triggers is attributed to IT, so an account switch during
      // the in-flight request can't mark the wrong user or install another
      // account's trial dates.
      const submittingUserId = useAuthStore.getState().user?.id;
      // The extension request is a trial request too (round-2 review): it
      // ends in `startTrialFromServer`, and the funnel counts every entry.
      trackTrialRequested('expiry_dialog', Boolean(submittingUserId));

      const result = await extendServerTrial(reason, details);
      const sameUser = useAuthStore.getState().user?.id === submittingUserId;

      if (result.success) {
        const extended = Boolean(result.trial);
        if (extended) {
          markExtensionUsedFor(submittingUserId);
          // `startTrialFromServer` records trialUserId=submitter, so even in
          // the switched-account edge the dates never leak to the new user —
          // but skip the local apply (and token cache) entirely then; the
          // submitter's next sign-in restores their trial from the server.
          if (sameUser) {
            useLicenseStore
              .getState()
              .startTrialFromServer(
                new Date(result.trial!.started_at),
                new Date(result.trial!.expires_at),
                submittingUserId,
                result.trial!.is_new
              );
          }
        }
        if (sameUser && result.token && window.electronAPI?.licenseCache) {
          try {
            await window.electronAPI.licenseCache.updateCacheWithSignedToken(result.token);
          } catch (cacheErr) {
            console.warn('[TrialExpiry] Failed to cache extended-trial token:', cacheErr);
          }
        }
        markShownFor(promptedExpiryIsoRef.current);
        openRef.current = false;
        setIsOpen(false);
        return { success: true, extended };
      }

      if (result.errorCode === 'ALREADY_EXTENDED') {
        markExtensionUsedFor(submittingUserId);
        return {
          success: false,
          error: 'This account already used its one-time trial extension.',
        };
      }
      return {
        success: false,
        error: result.error || 'Could not extend the trial. Please try again later.',
      };
    },
    [markShownFor]
  );

  return {
    isOpen,
    /** False once this machine knows THIS account's one-time extension is spent. */
    extensionAvailable: !isExtensionUsedFor(userId),
    onUpgrade,
    onContinuePersonal,
    onDismiss,
    onFeedbackOpened,
    onSubmitFeedback,
  };
}
